import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Form, InputNumber, Spin } from 'antd';
import OWRadioGroup from '../ow_radio_group/OWRadioGroup';
import {
	nullSafeGet,
	nullSafeGetOrElse,
	renderCurrencyByCurrencyId,
	roundOffToDecimal,
} from '../../utils/DataAccessUtils';
import { connect } from 'react-redux';

interface PartEquipmentCostFormFieldProps {
	currentUser?: any;
	unitCost: Number;
	entityId: Number;
	entityName: string;
	vendorId: Number;
	fetchMethod: any;
	setFieldsValue: any;
	form: any;
	currencyId: any;
	quantity: number;
	displayTotalCostOnlyWithValue?: boolean;
}

const PartEquipmentCostFormField: FC<PartEquipmentCostFormFieldProps> = ({
	entityId,
	entityName,
	vendorId,
	fetchMethod,
	setFieldsValue,
	form,
	currencyId,
	quantity,
	displayTotalCostOnlyWithValue,
}) => {
	const unitCost = Form.useWatch('unitCost', form);

	const [catalog, setCatalog] = useState(null);
	const [catalogLoading, setCatalogLoading] = useState(false);
	const [selectedCost, setSelectedCost] = useState(null);

	const refreshCatalog = useCallback(
		(id, partEquipmentVendorId) => {
			setCatalogLoading(true);
			fetchMethod(
				{
					[entityName]: id,
					partEquipmentVendorId,
				},
				'add_in_pr_index'
			)
				.then((res) => {
					const catalog = res && res.length > 0 ? res[0] : null;
					setCatalog(catalog);
				})
				.finally(() => {
					setCatalogLoading(false);
				});
		},
		[entityName, fetchMethod]
	);

	useEffect(() => {
		unitCost &&
			(parseFloat(nullSafeGetOrElse('lastPrice', catalog, 0)) === parseFloat(unitCost) ||
				parseFloat(nullSafeGetOrElse('listPrice', catalog, 0)) === parseFloat(unitCost)) &&
			setSelectedCost(`${unitCost}`);
	}, [catalog, unitCost]);

	useEffect(() => {
		entityId && vendorId && refreshCatalog(entityId, vendorId);
	}, [entityId, refreshCatalog, vendorId]);

	useEffect(() => {
		setFieldsValue({
			cost: roundOffToDecimal((unitCost || 0) * (quantity || 0)),
		});
	}, [unitCost, quantity, setFieldsValue]);

	const onPriceSelect = useCallback(
		(unitCost) => {
			setFieldsValue({ unitCost: unitCost ? roundOffToDecimal(unitCost) : unitCost });
			setSelectedCost(unitCost);
		},
		[setFieldsValue]
	);

	const catalogCurrencyId = useMemo(
		() => nullSafeGetOrElse('partEquipmentVendor.currencyId', catalog, currencyId),
		[catalog, currencyId]
	);

	const canShowTotalCost = useMemo(
		() => !displayTotalCostOnlyWithValue || (quantity && unitCost),
		[displayTotalCostOnlyWithValue, quantity, unitCost]
	);

	return (
		<>
			<Form.Item
				name="unitCost"
				label={
					<div className="flex flex-row items-center">
						Unit Cost
						{catalogLoading ? (
							<div className="ml-2">
								<Spin size="small" />
							</div>
						) : nullSafeGet('lastPrice', catalog) || nullSafeGet('listPrice', catalog) ? (
							<div className="ml-2">
								<OWRadioGroup
									value={selectedCost}
									onChange={onPriceSelect}
									disabled={catalogLoading}
									valueAccessor={(item) => item.value}
									renderRecord={(item) => item.label}
									items={[
										...(nullSafeGet('listPrice', catalog)
											? [
													{
														label: `List Price ${renderCurrencyByCurrencyId(catalogCurrencyId)(
															nullSafeGet('listPrice', catalog)
														)}`,
														value: nullSafeGet('listPrice', catalog),
													},
											  ]
											: []),
										...(nullSafeGet('lastPrice', catalog)
											? [
													{
														label: `Last Price ${renderCurrencyByCurrencyId(catalogCurrencyId)(
															nullSafeGet('lastPrice', catalog)
														)}`,
														value: nullSafeGet('lastPrice', catalog),
													},
											  ]
											: []),
									]}
								/>
							</div>
						) : null}
					</div>
				}
				className="w-100"
			>
				<InputNumber style={{ width: '100%' }} addonAfter={catalogCurrencyId} />
			</Form.Item>
			{canShowTotalCost && (
				<Form.Item name="cost" label="Total Cost">
					<InputNumber disabled step={0.01} style={{ width: '100%' }} addonAfter={currencyId} />
				</Form.Item>
			)}
		</>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
});

export default connect(mapStateToProps)(PartEquipmentCostFormField);
